import React, { useEffect, useRef, useState } from "react";
import { ArrowUpDown, ChevronDown, ChevronUp } from "lucide-react";
import { DEFAULT_LIST_SORT, LIST_SORT_OPTIONS } from "../utils/propertySort";

// Date picks carry a pink edge and price picks a blue one, as in Christian's
// mock-up, so the two pairs read as two groups.
const GROUP_EDGE = {
    date: "border-l-[#F472B6]",
    price: "border-l-[#55C5D0]",
};

// The Sort button that sits on top of the search bar on Collings Properties
// and My Listings. See utils/propertySort.js.
export default function PropertySortDropdown({ value = DEFAULT_LIST_SORT, onChange, disabled = false, className = "" }) {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        const close = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", close);
        document.addEventListener("touchstart", close);
        return () => {
            document.removeEventListener("mousedown", close);
            document.removeEventListener("touchstart", close);
        };
    }, [isOpen]);

    const selected = LIST_SORT_OPTIONS.find((o) => o.id === value) || LIST_SORT_OPTIONS[0];

    return (
        <div className={`relative ${className}`} ref={ref}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen((open) => !open)}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={`Sort: ${selected.label}`}
                className={`flex items-center justify-between gap-2 h-10 px-3 sm:px-4 min-w-[220px] bg-white rounded-lg border border-gray-200 text-xs sm:text-sm font-medium text-[#36423F] hover:border-[#55C5D0] transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
                <span className="flex items-center gap-2 truncate">
                    <ArrowUpDown className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-gray-400">Sort:</span>
                    <span className="truncate">{selected.label}</span>
                </span>
                {isOpen
                    ? <ChevronUp size={16} className="text-gray-400 shrink-0" />
                    : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
            </button>

            {isOpen && (
                <div
                    role="listbox"
                    className="absolute right-0 top-full mt-1 w-full min-w-[220px] bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden py-1"
                >
                    {LIST_SORT_OPTIONS.map((option) => {
                        const isSelected = option.id === selected.id;
                        return (
                            <button
                                key={option.id}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onClick={() => {
                                    onChange(option.id);
                                    setIsOpen(false);
                                }}
                                className={`w-full px-4 py-2.5 text-left text-xs sm:text-sm font-medium border-l-4 ${GROUP_EDGE[option.group]} transition-colors ${isSelected
                                    ? "bg-[#55C5D0] text-white"
                                    : "text-[#36423F] hover:bg-gray-50 active:bg-gray-100"
                                    }`}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
